import React from "react";
import { BCMSDefaultModel, BCMSStateManagerCRUD, Branding, DisabledPrompt, useParams, BCMSRecord, useBCMSContext, ModalOpen, StateManager, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import Classes, { Merchant, Card, CardOnFileMerchant, Transaction } from "model";

import MainContentContainer, { MainContentHeader, MainContentSection } from "components/shared/containers/MainContentContainer";
import BaseRowView from "components/shared/views/BaseRowView";
import MerchantDetailsView from "components/shared/views/MerchantDetailsView";

import "./styles.css";
import UIRadio from "components/ui/UIRadio";
import { UIPrimaryButton } from "components/ui/UIButton";
import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
/**
 * @function MerchantDetailsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for MerchantDetailsPage
 */

export default function MerchantDetailsPage(props) {
    const bcmsContext = useBCMSContext();
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, bcmsContext);
    BCMSRecord.globalControllerFor([ CardOnFileMerchant ], globalController);
    // Get the card id from the parameter
    const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      update: type => (val, label) => {
        const _label = label.toLowerCase();
        const crud = BCMSStateManagerCRUD.init(state);
        crud
        .update([ "data", "cardOnFileMerchant", type ], _label)
        .update([ "ui" ], ui)
        .submit(setState);
      },

      next: () => nextStep({ data, ui }),

      updateMerchantStatus: () => {
        const copyCardOnFileMerchantRecord = { ...data.cardOnFileMerchant.record };
        copyCardOnFileMerchantRecord.status = "updated";

        BCMSRecord.batch([
          CardOnFileMerchant.update({ ...copyCardOnFileMerchantRecord })
        ], globalController, Classes);

        const crud = BCMSStateManagerCRUD.init(state);
        crud
        .update([ "data", "cardOnFileMerchant", "status" ], "updated")
        .update([ "ui" ], ui)
        .submit(setState);

        window.postMessage({ message: { renderPageContent: true }});

        goStep(0);
      },

      back: () => goStep(0)
    });
  
    return (
      <StateManager state={ { data, ui } } controller={ controller }>
        <PageContent/>
      </StateManager>
    );
  }
  
  export const getDefaultData = merchantID => {
    const cardOnFileMerchant = BCMSDefaultModel.cardOnFileMerchant();
    merchantID = merchantID ? merchantID : "0";
  
    return {
      cardOnFileMerchant,
      cardOnFileSelectedTabType: "recurring",
      merchantID,
    };
  }
  
  export const getDefaultUI = () => ({
  
  });
  
  function PageContent(props) {
    const { data, ui, back } = useStateManager();
    const cardID = useParams().cardID ?? "0";
    const merchantID = data.merchantID ?? "0";
    //const location = useParams().locationID ?? "recurring";
    const location = data.cardOnFileSelectedTabType;
  
    const controller = useBCMSContext();
    BCMSRecord.globalControllerFor([ Merchant, Card, Transaction ], controller);
  
    const merchant = Merchant.initByID(merchantID);
    const card = Card.initByID(cardID);
    const hasCardOnFileMerchant = data.cardOnFileMerchant ?? false;
    let cardOnFileMerchantStatus = "";
    if(hasCardOnFileMerchant) {
      cardOnFileMerchantStatus = data.cardOnFileMerchant.status;
    }
  
    const transactions = Transaction.initAll()
      .filter(item => item.merchant.id === merchantID && item.card.id === cardID, [])
      .sort((a, b) => {
        if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
        else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
        else { return 0; }
      }, [])
      .map(item => <MerchantDetailsTransactionRow key={ `Transaction_${ item.id }` } transaction={ item } />, []);

  return (
    <MainContentContainer className="merchant-details-page" header={<MainContentHeader text="Merchant Details" onClick={ back } /> }>
      <MerchantDetailsView merchant={ merchant } card={ card }>
        { location === "recurring" && <p className="recurring-payments sub-text">Recurring Payments</p> }
        { location === "cardonfile" && <p className="sub-text card-on-file">Card on File</p> }
      </MerchantDetailsView>
      <Branding id="push_to_merchant_chw23" value="true">
        { cardOnFileMerchantStatus === "update card" &&
        <UpdateCardDetailsWidget />
        }
        { cardOnFileMerchantStatus === "need feedback" &&
        <StoreCardWithMerchantWidget />
        }
      </Branding>

      <MainContentSection text="Transactions">
        { transactions }
      </MainContentSection>
    </MainContentContainer>
  );
}

function MerchantDetailsTransactionRow(props) {
  const transaction = props.transaction ?? Transaction.firstInstanceOf();

  const name = transaction.merchant.name;
  const date = transaction.purchaseDate.format("M d, Y");
  const amount = transaction.amount.format();
  const status = transaction.status === "Pending" ? transaction.status : "";

  return (
    <DisabledPrompt>
      <BaseRowView className="merchant-detail-row" mainText={ name } subText={ date }>
        <div className="flex-container flex-vertical">
          <p>{ amount }</p>
          <p className="sub-text">{status}</p>
        </div>
      </BaseRowView>
    </DisabledPrompt>
  );
}

/**
 * @function UpdateCardDetailsWidget
 * @return {React.Component}
 * @exported false
 * @description Shows the question for updating the card details
 */

function UpdateCardDetailsWidget(props) {
  const { data, ui, back, next, update } = useStateManager();
  const cardOnFileMerchant = data.cardOnFileMerchant ?? {};

  return(
    <div className="update-card-details-widget">
      <FormContainer className="grey-section">
        <FormRow>
          <p className="grey-label bottom-border">You can update your new card details with this merchant, without having to enter the billing details or leaving this site. Do you want to continue with the update?</p>
          <UIRadio label="Yes" className="bottom-border" value={ cardOnFileMerchant.doYouWantToContinueWithTheUpdate === "yes" }
           onChange={ update("doYouWantToContinueWithTheUpdate") } />
          <UIRadio label="No" className="bottom-border" value={ cardOnFileMerchant.doYouWantToContinueWithTheUpdate === "no" }
           onChange={ update("doYouWantToContinueWithTheUpdate") } />
        </FormRow>
        <FormButtonContainer className="gray-widget-button-container">
          { cardOnFileMerchant.doYouWantToContinueWithTheUpdate === "yes" &&
          <UIPrimaryButton text="Continue" onClick={ next } />
          }
          { cardOnFileMerchant.doYouWantToContinueWithTheUpdate === "no" &&
          <ModalOpen id="UnchangedCardOnFileModal" data={ { onOKClick: back } }>
            <UIPrimaryButton text="Continue" />
          </ModalOpen>
          }
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}


/**
 * @function StoreCardWithMerchantWidget
 * @return {React.Component}
 * @exported false
 * @description Shows the question for storing the card details with the merchant
 */

function StoreCardWithMerchantWidget(props) {
  const { data, ui, back, next, update, updateMerchantStatus } = useStateManager();
  const cardOnFileMerchant = data.cardOnFileMerchant ?? {};

  return(
    <div className="store-card-with-merchant-widget">
      <FormContainer className="grey-section">
        <FormRow>
          <p className="grey-label bottom-border">Help us keep this list up-to-date. Did you store your card with this merchant?</p>
          <UIRadio label="Yes" className="bottom-border" value={ cardOnFileMerchant.didYouStoreYourCardWithThisMerchant === "yes" }
           onChange={ update("didYouStoreYourCardWithThisMerchant") } />
          <UIRadio label="No" className="bottom-border" value={ cardOnFileMerchant.didYouStoreYourCardWithThisMerchant === "no" }
           onChange={ update("didYouStoreYourCardWithThisMerchant") } />
        </FormRow>
        <FormButtonContainer className="gray-widget-button-container">
          { cardOnFileMerchant.didYouStoreYourCardWithThisMerchant === "yes" &&
          <ModalOpen id="UpdatedCardOnFileModal" data={ { onOKClick: updateMerchantStatus } }>
            <UIPrimaryButton text="Save" />
          </ModalOpen>
          }
          { cardOnFileMerchant.didYouStoreYourCardWithThisMerchant === "no" &&
          <ModalOpen id="UnchangedCardOnFileV2Modal" data={ { onOKClick: back } }>
            <UIPrimaryButton text="Save" />
          </ModalOpen>
          }
          {/*<ModalOpen id="UpdatedCardOnFileModal" >
            <UIPrimaryButton text="Save" />
          </ModalOpen>*/}
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}